import { SyntheticEvent, useState, useMemo, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, ThemeProvider, useTheme } from '@mui/material';
import composeTheme from '@theme/theme';
import { Tabs } from '@components/tabs';
import { useSearchDocuments } from '@services/document';
import { useGetPolicy, useSearchPolicyTransactions } from '@services/policy';
import { Layout } from '@layout/Layout';
import { useSearchSubjectivities } from '@services/subjectivity';
import { defaultItems, defaultSubtitle, defaultTitle } from '@pages/Forbidden/Forbidden.constants';
import { Forbidden } from '@pages/Forbidden';

import {
  AssociatedPoliciesTab,
  PolicyDetailsPanel,
  PolicyDocumentsTab,
  PolicySubjectivitiesTab,
  PolicyTermsTab,
  TransactionHistoryTab,
} from '@pages/PolicyDetails/panels';
import { PolicyTabs } from '@pages/PolicyDetails/PolicyDetails.constants';
import { useOktaAuth } from '@okta/okta-react';
import { QueryStatus } from '@models/queryStatus';
import { WidthContainer } from '@layout/WidthContainer';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';

export const PolicyDetailsPage = () => {
  const { oktaAuth } = useOktaAuth();
  const { policyId } = useParams();
  const { isCustomerUser, isInternalUser } = useStore((state) => state.userAuth, shallow);
  const { documents } = useSearchDocuments({
    policyId,
    pageOffset: 0,
    pageLimit: 10,
  });
  const { policyTransactions } = useSearchPolicyTransactions({
    policyId: policyId ?? '',
    pageOffset: 0,
    pageLimit: 10,
    includeCanceled: false,
  });
  const { subjectivities } = useSearchSubjectivities({ policyId });
  const { policy, status } = useGetPolicy({ policyId });
  const [searchParams, setSearchParams] = useSearchParams();
  const tabSelected = searchParams.get('tab');

  const theme = useTheme();
  const tabs = useMemo(() => {
    return (
      [
        {
          value: PolicyTabs.Documents,
          label: 'Documents',
          searchParam: 'documents',
          quantity: documents?.pagination.total.toString() ?? '0',
          tabContent: <PolicyDocumentsTab />,
        },
        {
          value: PolicyTabs.Subjectivities,
          hidden: isCustomerUser,
          label: 'Subjectivities',
          searchParam: 'subjectivities',
          quantity: subjectivities?.pagination.total.toString() ?? '0',
          tabContent: <PolicySubjectivitiesTab />,
          styles:
            subjectivities?.items.find((s) => !s.isBrokerSubjectivity && s.status === 'Active') !== undefined
              ? {
                  '.MuiChip-root': {
                    backgroundColor: `${theme.palette.error.light} !important`,
                    color: `${theme.palette.common.white} !important`,
                  },
                }
              : undefined,
        },
        {
          value: PolicyTabs.TransactionHistory,
          label: 'Transaction History',
          searchParam: 'transactionHistory',
          quantity: policyTransactions?.pagination.total.toString() ?? '0',
          tabContent: <TransactionHistoryTab />,
        },
        {
          value: PolicyTabs.PolicyTerms,
          hidden: !isInternalUser,
          label: 'Policy Terms',
          searchParam: 'policyTerms',
          tabContent: <PolicyTermsTab />,
        },
        {
          value: PolicyTabs.AssociatedPolicies,
          label: 'Associated Policies',
          searchParam: 'associatedPolicies',
          quantity: [
            ...(policy?.relatedPolicyDestinations ?? []),
            ...(policy?.relatedPolicySources ?? []),
          ].length.toString(),
          tabContent: <AssociatedPoliciesTab />,
        },
      ]?.filter((x) => !x.hidden) ?? []
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    documents?.pagination.total,
    policyTransactions?.pagination.total,
    subjectivities?.pagination.total,
    subjectivities?.items,
    policy?.relatedPolicyDestinations,
    policy?.relatedPolicySources,
    isCustomerUser,
    isInternalUser,
  ]);
  const [tabValue, setTabValue] = useState(tabs[0].value);
  const selectedTab = useMemo(() => tabs.find((t) => t.value === tabValue), [tabs, tabValue]);

  useEffect(() => {
    const hasTabSearchParam = searchParams.has('tab');
    if (!hasTabSearchParam) {
      const newParams = new URLSearchParams();
      newParams.set('tab', 'documents');
      setSearchParams(newParams, { replace: true });
    } else {
      const tab = tabs.find((t) => t.searchParam === tabSelected);
      if (tab) {
        setTabValue(tab.value);
      }
    }
  }, [searchParams, setSearchParams, tabs, tabSelected]);

  const handleChange = (event: SyntheticEvent, selectedTab: number) => {
    const tab = tabs.find((t) => t.value === selectedTab);
    const newParams = new URLSearchParams();
    newParams.set('tab', tab?.searchParam ?? '');
    setSearchParams(newParams, { replace: true });
    setTabValue(selectedTab);
  };

  // hide policy page when policy is not found
  if (status === QueryStatus.Error) {
    return (
      <Forbidden
        title={defaultTitle}
        subtitle={defaultSubtitle}
        items={defaultItems}
        primaryBtnText='Continue to your Homepage'
        primaryBtnRedirect='/'
        secondaryBtnText='Log out'
        secondaryBtnAction={async () => {
          await oktaAuth.signOut();
        }}
      />
    );
  }

  return (
    <Layout title='Policy Details' config={{ padding: 2 }}>
      <WidthContainer>
        <Box sx={{ mt: { xs: 1, sm: 5 }, width: '100%' }}>
          <PolicyDetailsPanel policyId={policyId} />
          <Box sx={{ mt: 5, ml: 1 }}>
            <Tabs tabOptions={tabs} value={tabValue} onChange={handleChange} />
          </Box>
          <ThemeProvider theme={composeTheme('light')}>{selectedTab?.tabContent}</ThemeProvider>
        </Box>
      </WidthContainer>
    </Layout>
  );
};
